import _ from "lodash";
import update from "immutability-helper";
import {
  CONTENT_UID_TEXT_BOX,
  QUESTION_UID_MULTI_PART,
  QUESTION_UID_SEPARATOR,
} from "../../modules/WorksheetConstants";

/** internal */
import {
  writeQuestionSubmissionConnectionInCache,
  getStudentQuestionDetailsQueryFromCacheV2,
  getQuestionSubmissionConnectionFromCache,
} from "Worksheet/modules/WorksheetGraphqlHelpers";
import {
  getIsQuestionAttempted,
  getQuestionSubmissionItem,
  isVisualWorksheetElement,
} from "Worksheet/modules/WorksheetUtils";

/**
 * Calculates the number of attempted questions in a worksheet from flat data structure
 * @param {Array} questions - Flat array of questions from the V2 query
 * @param {Object} submissions - Object containing submission data with edges array
 * @returns {number} - Count of attempted questions
 */
export const getAttemptedQuestionCountV2 = ({
  questions = [],
  submissions = {},
}) => {
  let attemptedQuestionCount = 0;

  if (!_.isEmpty(questions) && !_.isEmpty(submissions)) {
    const submissionNodes = _.map(_.get(submissions, "edges", []), "node");

    // Process each question
    questions?.forEach(question => {
      const questionTypeUid = _.get(question, "questionType.uid");
      const questionId = _.get(question, "id");

      switch (questionTypeUid) {
        case CONTENT_UID_TEXT_BOX:
        case QUESTION_UID_SEPARATOR:
          /** not a question - skip visual elements */
          break;
        case QUESTION_UID_MULTI_PART: {
          /** For multi-part questions, check if all sub-questions are attempted */
          const subQuestions = _.filter(
            questions,
            subQuestion =>
              _.isEqual(_.get(subQuestion, "parentIdV2"), questionId) &&
              _.get(subQuestion, "parentTypeV2") === "QUESTION"
          );

          let areSubQuestionsAttempted = true;
          let hasActualSubQuestions = false;

          subQuestions?.forEach(subQuestion => {
            const subQuestionTypeUid = _.get(subQuestion, "questionType.uid");

            // Skip visual worksheet elements
            if (isVisualWorksheetElement(subQuestionTypeUid)) return;

            hasActualSubQuestions = true;
            const submission = getQuestionSubmissionItem({
              questionId: subQuestion.id,
              submissions: submissionNodes,
            });

            areSubQuestionsAttempted =
              areSubQuestionsAttempted &&
              getIsQuestionAttempted({
                question: subQuestion,
                submission,
              });
          });

          // Only count as attempted if there are actual sub-questions and all are attempted
          if (hasActualSubQuestions && areSubQuestionsAttempted) {
            attemptedQuestionCount++;
          }

          break;
        }
        default: {
          // Only count top-level questions or sub-questions that are not part of multi-part
          // Skip if this question is a child of a multi-part question (it will be counted with its parent)
          const parentQuestion = _.find(
            questions,
            q => q?.id === _.get(question, "parentIdV2")
          );
          const isChildOfMultiPart =
            parentQuestion &&
            _.get(parentQuestion, "questionType.uid") ===
              QUESTION_UID_MULTI_PART;

          if (!isChildOfMultiPart) {
            const submission = getQuestionSubmissionItem({
              questionId,
              submissions: submissionNodes,
            });

            if (getIsQuestionAttempted({ question, submission })) {
              attemptedQuestionCount++;
            }
          }

          break;
        }
      }
    });
  }

  return attemptedQuestionCount;
};

/**
 * V2 version of updateQuestionSubmissionInCache that supports the new query format.
 * Updates the attempted status of questions within a specific submission in the cache.
 * This function performs two main updates: it first updates the attempted status of a single question,
 * then updates the count of attempted questions within the entire submission based on an assessment.
 *
 * @param {Object} question - The question object whose attempted status is to be updated.
 * @param {string} submissionId - The ID of the submission containing the question.
 * @param {Object} parentEntityDetails - The details of the parent entity.
 * @param {string} portalType - The portal type indicating the context in which the assessment is used.
 * @param {string} userType - The user type of the current user.
 * @param {string} mode - The mode in which the assessment is being accessed.
 */
export const updateQuestionSubmissionInCacheV2 = ({
  question,
  submissionId,
  portalType,
  parentEntityDetails,
}) => {
  const userFilter = "SELF";
  let updatedData = {};
  const assignmentSubmissionId = _.get(parentEntityDetails, "submissionId");

  const submissionCacheData = getQuestionSubmissionConnectionFromCache({
    id: assignmentSubmissionId,
    userFilter,
  });

  const questionSubmissionData = _.find(
    _.get(submissionCacheData, "edges", []),
    edge => _.isEqual(_.get(edge, "node.id"), submissionId)
  );
  // Get worksheet data to calculate attempted question count
  const assessmentQuestionsQueryVariables = {
    id: _.get(parentEntityDetails, "id"),
    type: _.get(parentEntityDetails, "type"),
    portalType,
  };

  const worksheetData = getStudentQuestionDetailsQueryFromCacheV2(
    assessmentQuestionsQueryVariables
  );

  const questions = _.get(worksheetData, "questions.edges", []);
  const attemptedQuestionCount = getAttemptedQuestionCountV2({
    questions,
    submissions: submissionCacheData,
  });

  const isQuestionAttempted = getIsQuestionAttempted({
    question,
    submission: _.get(questionSubmissionData, "node"),
  });

  // Update the cache data
  const edgeIndex = _.findIndex(_.get(submissionCacheData, "edges", []), edge =>
    _.isEqual(_.get(edge, "node.id"), submissionId)
  );

  if (edgeIndex >= 0) {
    updatedData = update(submissionCacheData, {
      totalQuestionAttempted: { $set: attemptedQuestionCount },
      edges: {
        [edgeIndex]: {
          node: {
            isQuestionAttempted: { $set: isQuestionAttempted },
          },
        },
      },
    });

    writeQuestionSubmissionConnectionInCache({
      id: assignmentSubmissionId,
      data: updatedData,
      userFilter,
    });
  }
};
